/**
 * Side-sheet for adding a new apartment or editing an existing one (COL-225).
 *
 * One component serves both flows: pass `apartment` to edit, or leave it
 * undefined to create a new apartment under `propertyId`. Mirrors the patterns
 * in RoomEditSheet — sectioned cards, blur-mode numeric inputs validated via
 * parseOptionalNumber, ⌘+Enter to save.
 *
 * Surfaces the apartment fields requested in COL-225: size (m²), number of
 * rooms, rental status (Vacant / Occupied) and unit/floor (e.g. "EG Links"),
 * alongside the owner link and commercial terms.
 */
import { useEffect, useState } from "react";
import { AlertTriangle, Loader2, Plus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InheritanceBadge } from "@/components/admin/v2/InheritanceBadge";
import { PaymentMethodRow } from "@/components/admin/v2/PaymentMethodRow";
import {
  OWNER_COLLECTS_LOCK_REASON,
  PAYMENT_METHODS,
  methodStateLabel,
  methodsLockedByCollectionMode,
  resolvePaymentMethods,
} from "@/lib/payment-methods";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Section } from "@/components/admin/v2/primitives/Section";
import { Field } from "@/components/admin/v2/primitives/Field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LessorCombobox } from "@/components/admin/v2/LessorCombobox";
import { LessorFormDialog } from "@/components/admin/v2/LessorFormDialog";
import { parseOptionalNumber } from "@/lib/v2-numeric";
import { normalizeVirtualTourUrl } from "@/lib/virtual-tour-url";
import { formatSupabaseError } from "@/lib/format-supabase-error";
import {
  resolvePlatformAccount,
  useApartmentBankOverride,
  fetchPropertyRoomsSnapshot,
  useCreateApartment,
  useCreateRoom,
  useDeleteApartmentBankOverride,
  usePlatformBankAccounts,
  usePlatformLessor,
  useProperty,
  usePropertyRooms,
  useUpdateApartment,
  useUpsertApartmentBankOverride,
} from "@/hooks/v2/useProperties";
import { useCreateLessor, useLessor } from "@/hooks/v2/useLessors";
import { useLessorIdByOwnerUser } from "@/hooks/v2/owner-user-lessor";
import {
  APARTMENT_RENTAL_STATUSES,
  COLLECTION_MODES,
  PAYOUT_METHODS,
  PICKUP_METHODS,
  REVENUE_MODELS,
  type Apartment,
  type ApartmentRentalStatus,
  type CollectionMode,
  type PayoutMethod,
  type PickupMethod,
  type RevenueModel,
} from "@/types/property";
import type { LessorInput } from "@/types/lessor";
import { collectionModeLabels } from "@/lib/collection-mode";

const INHERIT = "__inherit__";

/** A payment method at apartment level: follow the property, or state a value. */
type MethodOverride = typeof INHERIT | "on" | "off";

function methodOverrideFromColumn(value: boolean | null): MethodOverride {
  if (value == null) return INHERIT;
  return value ? "on" : "off";
}

function methodOverrideToColumn(value: MethodOverride): boolean | null {
  if (value === INHERIT) return null;
  return value === "on";
}

// "Number of rooms" used to be display-only; now it drives a sequential
// insert per missing room on save. Cap it well above any real shared-flat
// apartment so a typo (e.g. an extra zero) can't queue up thousands of
// one-by-one network requests and DB rows. Exported: the property-creation
// wizard's "Number of rooms" (Step2Apartments.tsx) has the same failure mode
// and shares this cap.
export const MAX_ROOM_FILL = 60;

// Staff-facing: the logged-in user is not the owner, so no name.
const COLLECTION_MODE_LABELS = collectionModeLabels();

const REVENUE_MODEL_LABELS: Record<RevenueModel, string> = {
  full_rent: "Full rent (rent difference)",
  pct_of_rent: "Percentage of rent",
  flat_fee_per_room: "Flat fee per room",
};

const PICKUP_METHOD_LABELS: Record<PickupMethod, string> = {
  keynest: "KeyNest partner shop",
  manual: "Manual address (non-KeyNest)",
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyId: number;
  /** Omit / null for create mode; pass an apartment to edit it. */
  apartment?: Apartment | null;
  /** Suggested sort_order for a newly created apartment (max existing + 1). */
  nextSortOrder?: number;
  /**
   * Section to scroll into view when the sheet opens — set by ApartmentDetail
   * from `?edit=revenue`, the deep link a management-invoice row's "Payment
   * configuration" action follows (COL-287).
   */
  focusSection?: "revenue" | null;
};

/** DOM id of the section `focusSection` scrolls to. */
const SECTION_ID_REVENUE = "apartment-edit-section-revenue";

type FormState = {
  label: string;
  floor: string;
  size_sqm: string;
  num_rooms: string;
  rental_status: ApartmentRentalStatus | "";
  /** The property owner (owner = lessor by default). */
  owner_user_id: string | null;
  /** Optional lessor override (exception case). */
  lessor_id: string | null;
  /** Representor override for this apartment. Blank = inherit from the owner. */
  rep_override_name: string;
  rep_override_note: string;
  master_rent: string;
  lease_end: string;
  payout_method: PayoutMethod | "";
  invested_amount: string;
  owner_since: string;
  // Revenue / collection (INHERIT = inherit from property)
  collection_mode: CollectionMode | typeof INHERIT;
  // Reminder suppression override: INHERIT | "on" | "off".
  rent_reminders_enabled: typeof INHERIT | "on" | "off";
  revenue_model: RevenueModel | typeof INHERIT;
  coliviq_fee_pct: string;
  coliviq_fee_amount: string;
  platform_bank_account_id: string; // INHERIT or numeric id as string
  // Payment-method overrides. INHERIT = follow the property; "on"/"off" write a
  // value that this apartment's rooms resolve to instead.
  stripe_card_enabled: MethodOverride;
  bank_transfer_enabled: MethodOverride;
  sepa_debit_enabled: MethodOverride;
  // Owner-side bank override for this apartment
  bank_holder: string;
  bank_iban: string;
  bank_bic: string;
  bank_name: string;
  // Key pickup override (blank = inherit from property)
  keynest_pickup_location: string;
  keynest_pickup_address: string;
  pickup_method: PickupMethod | typeof INHERIT;
  pickup_instructions: string;
  // Virtual tour URL override (blank = inherit from property)
  virtual_tour_url: string;
};

const EMPTY: FormState = {
  label: "",
  floor: "",
  size_sqm: "",
  num_rooms: "",
  rental_status: "",
  owner_user_id: null,
  lessor_id: null,
  rep_override_name: "",
  rep_override_note: "",
  master_rent: "",
  lease_end: "",
  payout_method: "",
  invested_amount: "",
  owner_since: "",
  collection_mode: INHERIT,
  rent_reminders_enabled: INHERIT,
  revenue_model: INHERIT,
  coliviq_fee_pct: "",
  coliviq_fee_amount: "",
  platform_bank_account_id: INHERIT,
  stripe_card_enabled: INHERIT,
  bank_transfer_enabled: INHERIT,
  sepa_debit_enabled: INHERIT,
  bank_holder: "",
  bank_iban: "",
  bank_bic: "",
  bank_name: "",
  keynest_pickup_location: "",
  keynest_pickup_address: "",
  pickup_method: INHERIT,
  pickup_instructions: "",
  virtual_tour_url: "",
};

function fromApartment(a: Apartment): FormState {
  return {
    label: a.label ?? "",
    floor: a.floor ?? "",
    size_sqm: a.size_sqm != null ? String(a.size_sqm) : "",
    num_rooms: a.num_rooms != null ? String(a.num_rooms) : "",
    rental_status: (a.rental_status ?? "") as ApartmentRentalStatus | "",
    owner_user_id: a.owner_user_id ?? null,
    lessor_id: a.lessor_id ?? null,
    rep_override_name: a.rep_override?.name ?? "",
    rep_override_note: a.rep_override?.note ?? "",
    master_rent: a.master_rent != null ? String(a.master_rent) : "",
    lease_end: a.lease_end ?? "",
    payout_method: (a.payout_method ?? "") as PayoutMethod | "",
    invested_amount: a.invested_amount != null ? String(a.invested_amount) : "",
    owner_since: a.owner_since ?? "",
    collection_mode: a.collection_mode ?? INHERIT,
    rent_reminders_enabled:
      a.rent_reminders_enabled == null
        ? INHERIT
        : a.rent_reminders_enabled
          ? "on"
          : "off",
    revenue_model: a.revenue_model ?? INHERIT,
    coliviq_fee_pct: a.coliviq_fee_pct != null ? String(a.coliviq_fee_pct) : "",
    coliviq_fee_amount:
      a.coliviq_fee_amount != null ? String(a.coliviq_fee_amount) : "",
    platform_bank_account_id:
      a.platform_bank_account_id != null
        ? String(a.platform_bank_account_id)
        : INHERIT,
    stripe_card_enabled: methodOverrideFromColumn(a.stripe_card_enabled),
    bank_transfer_enabled: methodOverrideFromColumn(a.bank_transfer_enabled),
    sepa_debit_enabled: methodOverrideFromColumn(a.sepa_debit_enabled),
    bank_holder: "",
    bank_iban: "",
    bank_bic: "",
    bank_name: "",
    keynest_pickup_location: a.keynest_pickup_location ?? "",
    keynest_pickup_address: a.keynest_pickup_address ?? "",
    pickup_method: a.pickup_method ?? INHERIT,
    pickup_instructions: a.pickup_instructions ?? "",
    virtual_tour_url: a.virtual_tour_url ?? "",
  };
}

export function ApartmentEditSheet({
  open,
  onOpenChange,
  propertyId,
  apartment,
  nextSortOrder,
  focusSection = null,
}: Props) {
  const { toast } = useToast();
  const { t } = useLanguage();
  // This sheet is reachable by an owner-portal-only account editing their own
  // apartment (V2ApartmentDetail's Edit button, behind requireAdmin — owner
  // is console access too), not just staff. The representor requirement is
  // staff-only, same as the add-property wizard hiding it entirely for
  // ownerSubmitting: an owner shouldn't be blocked from saving an unrelated
  // field edit (floor, size, …) because no one has set a representor yet.
  const { isOwnerPortalOnly } = useAuth();
  // Scroll the deep-linked section into view once the sheet's content has
  // mounted (Radix renders it only while open — see PropertyEditSheet).
  useEffect(() => {
    if (!open || !focusSection) return;
    const id = window.setTimeout(() => {
      document
        .getElementById(SECTION_ID_REVENUE)
        ?.scrollIntoView({ block: "start", behavior: "smooth" });
    }, 80);
    return () => window.clearTimeout(id);
  }, [open, focusSection]);
  const createMut = useCreateApartment();
  const updateMut = useUpdateApartment();
  const createLessorMut = useCreateLessor();
  const bankUpsertMut = useUpsertApartmentBankOverride();
  const bankDeleteMut = useDeleteApartmentBankOverride();
  const createRoomMut = useCreateRoom();
  const isEdit = !!apartment;

  const {
    data: property,
    isLoading: propertyLoading,
    isError: propertyErrored,
  } = useProperty(propertyId);
  const { data: platformAccounts = [] } = usePlatformBankAccounts();
  const { data: coliviqLessor } = usePlatformLessor();
  const { data: bankOverride } = useApartmentBankOverride(
    apartment?.id ?? null
  );
  // Property-wide rooms — used on save to fill "number of rooms" up to the
  // requested count by creating bare rooms for the gap. Scoped to the whole
  // property (not just this apartment) because room titles are unique per
  // property (rooms_property_id_title_unique), so generated names must avoid
  // every room in the property, not just this apartment's.
  const {
    data: propertyRooms = [],
    isLoading: roomsLoading,
    isError: roomsErrored,
  } = usePropertyRooms(propertyId);

  const [form, setForm] = useState<FormState>(() =>
    apartment ? fromApartment(apartment) : EMPTY
  );
  const [createOwnerOpen, setCreateOwnerOpen] = useState(false);

  // The linked owner (modeled as a lessor) — used to surface the representor
  // and bank defaults this apartment inherits. form.lessor_id is blank for an
  // apartment that names its owner only via owner_user_id and whose
  // apartments_fill_lessor_from_owner backfill was skipped (ambiguous
  // owner→lessor mapping) — bridge through the same owner-user-lessor map
  // ApartmentDetail.tsx and PropertySidebar.tsx already use for this exact
  // gap, so this apartment isn't wrongly treated as ownerless.
  const {
    data: lessorIdByOwnerUser = {},
    isLoading: lessorIdByOwnerUserLoading,
    isError: lessorIdByOwnerUserErrored,
  } = useLessorIdByOwnerUser();
  const effectiveOwnLessorId =
    form.lessor_id ??
    (form.owner_user_id
      ? (lessorIdByOwnerUser[form.owner_user_id] ?? null)
      : null);
  const {
    data: linkedOwner,
    isLoading: linkedOwnerLoading,
    isError: linkedOwnerErrored,
  } = useLessor(effectiveOwnLessorId);
  /** What the contract would use if this apartment sets no override. */
  const inheritedRepName = linkedOwner?.rep_name?.trim() ?? "";
  // Below the apartment's own owner, resolveEffectiveRep (useProperties.ts)
  // falls through to the PROPERTY's own rep_override, then the property's
  // own lessor — an apartment with no owner of its own can still resolve a
  // representor from either. Required for the save-blocking check below to
  // match what create-contract actually resolves, not just what this
  // apartment carries on its own.
  const {
    data: propertyLessor,
    isLoading: propertyLessorLoading,
    isError: propertyLessorErrored,
  } = useLessor(property?.lessor_id ?? null);
  // create-contract's last resort is the legacy flat properties.representor_name
  // column (still editable in the legacy console) — count it too, or a property
  // that only sets it would be blocked here while its contracts still resolve.
  const propertyRepName =
    property?.rep_override?.name?.trim() ||
    propertyLessor?.rep_name?.trim() ||
    property?.representor_name?.trim() ||
    "";
  // Whether any lookup feeding inheritedRepName/propertyRepName is still in
  // flight or failed — either way an empty reading isn't a real "no
  // representor". The owner-user→lessor map only matters when it's the bridge
  // this apartment actually needs (no lessor_id, but an owner_user_id); for
  // any other apartment its state is irrelevant and must not hold up the
  // actionable "Required" result.
  const repLookupPending =
    linkedOwnerLoading ||
    linkedOwnerErrored ||
    propertyLessorLoading ||
    propertyLessorErrored ||
    propertyLoading ||
    propertyErrored ||
    (!form.lessor_id &&
      !!form.owner_user_id &&
      (lessorIdByOwnerUserLoading || lessorIdByOwnerUserErrored));

  useEffect(() => {
    if (open) setForm(apartment ? fromApartment(apartment) : EMPTY);
  }, [open, apartment]);

  // Seed the owner-bank override fields once the override row loads (it's a
  // separate fetch from the apartment row).
  useEffect(() => {
    if (!open || !bankOverride) return;
    setForm((f) => ({
      ...f,
      bank_holder: bankOverride.holder ?? "",
      bank_iban: bankOverride.iban ?? "",
      bank_bic: bankOverride.bic ?? "",
      bank_name: bankOverride.bank_name ?? "",
    }));
  }, [open, bankOverride]);

  const isSubmitting =
    createMut.isPending ||
    updateMut.isPending ||
    bankUpsertMut.isPending ||
    createRoomMut.isPending;

  // Effective CoLiviq account given the current platform selection (or inherit).
  const effectivePlatformAccount = resolvePlatformAccount({
    apartment:
      form.platform_bank_account_id === INHERIT
        ? (apartment ?? null)
        : ({
            platform_bank_account_id: Number(form.platform_bank_account_id),
          } as Apartment),
    property: property ?? null,
    accounts: platformAccounts,
    coliviqLessor: coliviqLessor ?? null,
  });

  const effectiveCollectionMode: CollectionMode =
    form.collection_mode === INHERIT
      ? (property?.collection_mode ?? "company_collects")
      : form.collection_mode;
  const effectiveRevenueModel: RevenueModel =
    form.revenue_model === INHERIT
      ? (property?.revenue_model ?? "full_rent")
      : form.revenue_model;

  // What a room in this apartment would resolve to if the apartment inherited
  // everything — i.e. the property's own values, or the platform default where
  // the property states nothing. Drives the "Inherit from property (On)" hints.
  const propertyMethods = resolvePaymentMethods({ property: property ?? null });

  // owner_collects settles all three methods, so the selects show the outcome
  // and stop being editable rather than offering a choice the resolver overrules.
  const paymentMethodsLocked = methodsLockedByCollectionMode(
    effectiveCollectionMode
  );

  // Warn only when this edit is what switches direct debit on for these rooms.
  const sepaTurnedOn =
    form.sepa_debit_enabled === "on" ||
    (form.sepa_debit_enabled === INHERIT && propertyMethods.sepa_debit.enabled);

  function handleCreateOwner(input: LessorInput) {
    createLessorMut.mutate(input, {
      onSuccess: (lessor) => {
        toast({
          title: t("admin.apartments.ownerAdded", "Owner added"),
          description: lessor.name,
        });
        patch({ lessor_id: lessor.id });
        setCreateOwnerOpen(false);
      },
      onError: (err) =>
        toast({
          title: t("admin.apartments.couldNotAddOwner", "Could not add owner"),
          description: formatSupabaseError(err),
          variant: "destructive",
        }),
    });
  }

  function patch(p: Partial<FormState>) {
    setForm((f) => ({ ...f, ...p }));
  }

  async function handleSubmit() {
    if (!form.label.trim()) {
      toast({
        title: t(
          "admin.apartments.missingRequiredField",
          "Missing required field"
        ),
        description: t(
          "admin.apartments.labelRequired",
          "Apartment label is required (e.g. “Apartment 1”)."
        ),
        variant: "destructive",
      });
      return;
    }

    // A representor is required on every contract ("vertreten durch"). It
    // can resolve from this apartment's own override, its linked owner's
    // default, the property's own override, or the property's own lessor —
    // the same chain resolveEffectiveRep walks (see propertyRepName above).
    // Staff-only, see isOwnerPortalOnly above. Only relevant when there's no
    // typed override to begin with — an explicit rep_override_name always
    // wins regardless of whether the inherited queries have resolved yet.
    if (
      !isOwnerPortalOnly &&
      !form.rep_override_name.trim() &&
      !inheritedRepName &&
      !propertyRepName
    ) {
      // Only reach here when NEITHER an owner-level nor property-level
      // representor has already resolved — if either has, the loading/error
      // state of the other lookups is irrelevant to this save (a source
      // resolveEffectiveRep would never even consult once an earlier one
      // wins) and must not block it. Don't reject a save on an "empty"
      // reading that's actually still in flight — or failed — (see
      // repLookupPending; mirrors the roomsLoading/roomsErrored gate
      // below). propertyLoading covers the
      // cold-open window before `property` exists at all, during which the
      // property-lessor query below is disabled (no id yet) and so reports
      // no loading state of its own. A failed query is just as dangerous to
      // proceed on as a loading one: it also reads as "no representor" if
      // treated as an empty result.
      if (repLookupPending) {
        toast({
          title: t(
            "admin.apartments.stillLoadingOwnerData",
            "Still loading owner data"
          ),
          description: t(
            "admin.apartments.waitAndTrySavingAgain",
            "Please wait a moment and try saving again."
          ),
          variant: "destructive",
        });
        return;
      }
      toast({
        title: t(
          "admin.apartments.missingRequiredField",
          "Missing required field"
        ),
        description: t(
          "admin.apartments.representorRequired",
          "This apartment needs a representor — enter one, or link an owner whose default representor is set."
        ),
        variant: "destructive",
      });
      return;
    }

    // Validate numerics up-front so a typo surfaces clearly instead of a
    // generic Postgres "invalid input syntax for type numeric".
    const sizeSqm = parseOptionalNumber(form.size_sqm);
    if (!sizeSqm.ok) {
      toast({
        title: t("admin.apartments.invalidNumber", "Invalid number"),
        description: t(
          "admin.apartments.invalidSize",
          'Size "{{value}}" is not a valid number. Use digits only (e.g. 72).'
        ).replace("{{value}}", sizeSqm.raw),
        variant: "destructive",
      });
      return;
    }
    const numRooms = parseOptionalNumber(form.num_rooms);
    if (!numRooms.ok) {
      toast({
        title: t("admin.apartments.invalidNumber", "Invalid number"),
        description: t(
          "admin.apartments.invalidNumRooms",
          'Number of rooms "{{value}}" is not valid. Use a whole number (e.g. 3).'
        ).replace("{{value}}", numRooms.raw),
        variant: "destructive",
      });
      return;
    }
    if (numRooms.value != null && !Number.isInteger(numRooms.value)) {
      toast({
        title: t("admin.apartments.invalidNumber", "Invalid number"),
        description: t(
          "admin.apartments.numRoomsWholeNumber",
          "Number of rooms must be a whole number (e.g. 3)."
        ),
        variant: "destructive",
      });
      return;
    }
    if (numRooms.value != null && numRooms.value > MAX_ROOM_FILL) {
      toast({
        title: t(
          "admin.apartments.numRoomsTooLarge",
          "Number of rooms too large"
        ),
        description: t(
          "admin.apartments.numRoomsTooLargeDesc",
          "Enter at most {{max}} — larger counts would create that many rooms one by one on save."
        ).replace("{{max}}", String(MAX_ROOM_FILL)),
        variant: "destructive",
      });
      return;
    }
    // Only the room-fill below needs an accurate property-wide room list —
    // to count this apartment's existing rooms and avoid title collisions —
    // so only bail here when a fill would actually be attempted; an
    // unrelated room-query outage shouldn't block a plain label/owner edit.
    // A failed query falls back to an empty array just like a loading one,
    // so it's just as dangerous to proceed on — must require a successful
    // fetch before risking over-creating rooms (edit) or a title Postgres
    // will reject as a duplicate (create).
    if (
      numRooms.value != null &&
      numRooms.value > 0 &&
      (roomsLoading || roomsErrored)
    ) {
      toast({
        title: roomsErrored
          ? t(
              "admin.apartments.couldNotLoadRoomData",
              "Could not load room data"
            )
          : t(
              "admin.apartments.stillLoadingRoomData",
              "Still loading room data"
            ),
        description: t(
          "admin.apartments.waitAndTrySavingAgain",
          "Please wait a moment and try saving again."
        ),
        variant: "destructive",
      });
      return;
    }
    const masterRent = parseOptionalNumber(form.master_rent);
    if (!masterRent.ok) {
      toast({
        title: t("admin.apartments.invalidNumber", "Invalid number"),
        description: t(
          "admin.apartments.invalidMasterRent",
          'Master rent "{{value}}" is not a valid number.'
        ).replace("{{value}}", masterRent.raw),
        variant: "destructive",
      });
      return;
    }
    const investedAmount = parseOptionalNumber(form.invested_amount);
    if (!investedAmount.ok) {
      toast({
        title: t("admin.apartments.invalidNumber", "Invalid number"),
        description: t(
          "admin.apartments.invalidInvestedAmount",
          'Purchase price "{{value}}" is not a valid number.'
        ).replace("{{value}}", investedAmount.raw),
        variant: "destructive",
      });
      return;
    }
    const feePct = parseOptionalNumber(form.coliviq_fee_pct);
    if (!feePct.ok) {
      toast({
        title: t("admin.apartments.invalidNumber", "Invalid number"),
        description: t(
          "admin.apartments.invalidFeePercent",
          'Fee percent "{{value}}" is not a valid number.'
        ).replace("{{value}}", feePct.raw),
        variant: "destructive",
      });
      return;
    }
    const feeAmount = parseOptionalNumber(form.coliviq_fee_amount);
    if (!feeAmount.ok) {
      toast({
        title: t("admin.apartments.invalidNumber", "Invalid number"),
        description: t(
          "admin.apartments.invalidFlatFee",
          'Flat fee "{{value}}" is not a valid number.'
        ).replace("{{value}}", feeAmount.raw),
        variant: "destructive",
      });
      return;
    }

    const overrideRevenueModel =
      form.revenue_model === INHERIT ? null : form.revenue_model;

    // Representor override: blank name = inherit (null), same convention as the
    // other apartment-level overrides. The NAME carries the override; the note
    // only annotates it. A note without a name is dropped rather than stored,
    // because every reader of this column — the inheritance badge here and the
    // chain in create-contract — claims the branch on a non-blank name, so a
    // note-only row would persist and then surface nowhere.
    const repName = form.rep_override_name.trim();
    const repNote = form.rep_override_note.trim();
    const repOverridePayload = repName
      ? { name: repName, note: repNote || null }
      : null;

    const payload = {
      label: form.label.trim(),
      floor: form.floor.trim() || null,
      size_sqm: sizeSqm.value,
      num_rooms: numRooms.value,
      rental_status: form.rental_status === "" ? null : form.rental_status,
      owner_user_id: form.owner_user_id,
      lessor_id: form.lessor_id,
      rep_override: repOverridePayload,
      master_rent: masterRent.value,
      lease_end: form.lease_end || null,
      payout_method: form.payout_method === "" ? null : form.payout_method,
      invested_amount: investedAmount.value,
      owner_since: form.owner_since || null,
      // Revenue / collection overrides (null = inherit from property)
      collection_mode:
        form.collection_mode === INHERIT ? null : form.collection_mode,
      rent_reminders_enabled:
        form.rent_reminders_enabled === INHERIT
          ? null
          : form.rent_reminders_enabled === "on",
      revenue_model: overrideRevenueModel,
      // Only keep the fee param relevant to the chosen (override) model.
      coliviq_fee_pct:
        overrideRevenueModel === "pct_of_rent" ? feePct.value : null,
      coliviq_fee_amount:
        overrideRevenueModel === "flat_fee_per_room" ? feeAmount.value : null,
      platform_bank_account_id:
        form.platform_bank_account_id === INHERIT
          ? null
          : Number(form.platform_bank_account_id),
      // Payment methods: null = inherit the property. Written even when the
      // effective collection mode is owner_collects, because the resolver does
      // the clamping — the admin's choice survives a later mode change.
      stripe_card_enabled: methodOverrideToColumn(form.stripe_card_enabled),
      bank_transfer_enabled: methodOverrideToColumn(form.bank_transfer_enabled),
      sepa_debit_enabled: methodOverrideToColumn(form.sepa_debit_enabled),
      keynest_pickup_location: form.keynest_pickup_location.trim() || null,
      keynest_pickup_address: form.keynest_pickup_address.trim() || null,
      pickup_method: form.pickup_method === INHERIT ? null : form.pickup_method,
      pickup_instructions: form.pickup_instructions.trim() || null,
      virtual_tour_url: normalizeVirtualTourUrl(form.virtual_tour_url) || null,
    } satisfies Partial<Apartment>;

    // Owner-bank override: write when any field is filled, clear when all empty.
    const bankFilled =
      form.bank_holder.trim() ||
      form.bank_iban.trim() ||
      form.bank_bic.trim() ||
      form.bank_name.trim();

    let apartmentId = apartment?.id ?? null;
    try {
      if (isEdit && apartment) {
        await updateMut.mutateAsync({
          id: apartment.id,
          propertyId,
          patch: payload,
        });
      } else {
        const created = await createMut.mutateAsync({
          property_id: propertyId,
          sort_order: nextSortOrder ?? null,
          ...payload,
        });
        apartmentId = created.id;
      }

      if (apartmentId != null) {
        if (bankFilled) {
          await bankUpsertMut.mutateAsync({
            apartment_id: apartmentId,
            holder: form.bank_holder.trim() || null,
            iban: form.bank_iban.trim() || null,
            bic: form.bank_bic.trim() || null,
            bank_name: form.bank_name.trim() || null,
          });
        } else if (isEdit && bankOverride) {
          await bankDeleteMut.mutateAsync(apartmentId);
        }
      }
    } catch (err) {
      toast({
        title: t(
          "admin.apartments.couldNotSaveApartment",
          "Could not save apartment"
        ),
        description: formatSupabaseError(err),
        variant: "destructive",
      });
      return;
    }

    // The apartment itself is saved at this point — everything below is the
    // "number of rooms" fill, kept in its own try/catch so a mid-batch room
    // insert failure (e.g. a title collision from concurrent edits) reports
    // as a partial-rooms notice rather than a false "could not save
    // apartment", which would suggest the already-committed apartment save
    // needs retrying too.
    //
    // Fills up to the requested count by creating bare rooms for the gap —
    // it never removes rooms when the count goes down, since a room may
    // carry a tenant, history, or overrides. A new apartment always starts
    // at 0 existing rooms. Titles must be unique across the whole property
    // (rooms_property_id_title_unique), so they are generated against every
    // room in the property, not just this apartment's — a second apartment
    // can't reuse "Room 1" if another apartment already has one.
    let roomsCreated = 0;
    let roomsAttempted = 0;
    let roomFillError: unknown = null;
    if (apartmentId != null && numRooms.value != null && numRooms.value > 0) {
      // `propertyRooms` (from the mounted usePropertyRooms query) can be
      // however stale the sheet has been open, so re-read the room count and
      // titles directly right before computing the gap — this narrows, but
      // does not close, the window where a concurrent room creation
      // elsewhere goes unseen (only a DB-side atomic count+insert would
      // close it fully). If this fetch itself fails, report it the same way
      // as a failed insert rather than falling back to the stale cached
      // snapshot, which would reintroduce exactly the race this is meant to
      // shrink.
      try {
        const roomSnapshot = await fetchPropertyRoomsSnapshot(propertyId);
        const currentCount = isEdit
          ? roomSnapshot.filter((r) => r.apartment_id === apartmentId).length
          : 0;
        roomsAttempted = Math.max(0, numRooms.value - currentCount);
        if (roomsAttempted > 0) {
          const takenTitles = new Set(roomSnapshot.map((r) => r.title));
          const baseLabel = payload.label;
          let n = currentCount + 1;
          for (let i = 0; i < roomsAttempted; i++) {
            let title = `${baseLabel} — Room ${n}`;
            while (takenTitles.has(title)) {
              n++;
              title = `${baseLabel} — Room ${n}`;
            }
            takenTitles.add(title);
            n++;
            await createRoomMut.mutateAsync({
              property_id: propertyId,
              apartment_id: apartmentId,
              title,
              price: 0,
              // No photos or description yet — hidden so an empty, priceless
              // "available" room can't reach the public listing. Unhide from
              // the room detail page once it's fleshed out.
              is_hidden: true,
            });
            roomsCreated++;
          }
        }
      } catch (err) {
        roomFillError = err;
      }
    }

    if (roomFillError) {
      toast({
        title: t(
          "admin.apartments.savedButRoomCreationStoppedEarly",
          "Apartment saved, but room creation stopped early"
        ),
        description: t(
          "admin.apartments.roomsCreatedBeforeError",
          "{{created}} of {{attempted}} rooms were created before this error: {{error}}"
        )
          .replace("{{created}}", String(roomsCreated))
          .replace("{{attempted}}", String(roomsAttempted))
          .replace("{{error}}", formatSupabaseError(roomFillError)),
        variant: "destructive",
      });
      onOpenChange(false);
      return;
    }

    toast({
      title: isEdit
        ? t("admin.apartments.apartmentUpdated", "Apartment updated")
        : t("admin.apartments.apartmentCreated", "Apartment created"),
      description:
        roomsCreated > 0
          ? (roomsCreated === 1
              ? t(
                  "admin.apartments.roomsAddedHiddenNoteOne",
                  "{{label}} — {{count}} room added (hidden until you add photos and unhide each)"
                )
              : t(
                  "admin.apartments.roomsAddedHiddenNoteMany",
                  "{{label}} — {{count}} rooms added (hidden until you add photos and unhide each)"
                )
            )
              .replace("{{label}}", payload.label)
              .replace("{{count}}", String(roomsCreated))
          : payload.label,
    });
    onOpenChange(false);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      void handleSubmit();
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-y-auto"
        onKeyDown={handleKeyDown}
      >
        <SheetHeader className="space-y-1">
          <SheetTitle>
            {isEdit
              ? t("admin.apartments.editApartment", "Edit apartment")
              : t("admin.apartments.addApartment", "Add apartment")}
          </SheetTitle>
          <SheetDescription>
            {isEdit
              ? t(
                  "admin.apartments.updatesThisApartmentDirectly",
                  "Updates this apartment directly."
                )
              : t(
                  "admin.apartments.createNewOwnerOwnedUnit",
                  "Create a new owner-owned unit under this property."
                )}{" "}
            {t("admin.apartments.pressCmdEnterToSave", "Press")}{" "}
            <kbd className="font-mono text-[10px]">⌘ ↵</kbd>{" "}
            {t("admin.apartments.toSave", "to save.")}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 mt-4">
          {/* Basics */}
          <Section title={t("admin.field.apartment", "Apartment")}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field
                label={t("admin.apartments.labelField", "Label")}
                required
                className="sm:col-span-2"
              >
                <Input
                  value={form.label}
                  onChange={(e) => patch({ label: e.target.value })}
                  placeholder={t(
                    "admin.apartments.labelPlaceholder",
                    "e.g. Apartment 1"
                  )}
                />
              </Field>
              <Field label={t("admin.apartments.unitFloor", "Unit / Floor")}>
                <Input
                  value={form.floor}
                  onChange={(e) => patch({ floor: e.target.value })}
                  placeholder={t(
                    "admin.apartments.unitFloorPlaceholder",
                    "e.g. EG Links (Ground floor, left)"
                  )}
                />
              </Field>
              <Field label={t("admin.apartments.sizeSqm", "Size (m²)")}>
                <Input
                  type="number"
                  min={0}
                  step="0.1"
                  inputMode="decimal"
                  value={form.size_sqm}
                  onChange={(e) => patch({ size_sqm: e.target.value })}
                  placeholder="72"
                />
              </Field>
              <Field
                label={t("admin.apartments.numberOfRooms", "Number of rooms")}
                hint={t(
                  "admin.apartments.numberOfRoomsHint",
                  'On save, bare rooms are created to reach this count (named "{{label}} — Room 1", "— Room 2", …), hidden from public listings until you add photos and unhide each. Lowering it later does not delete existing rooms.'
                ).replace(
                  "{{label}}",
                  form.label.trim() ||
                    t("admin.apartments.apartmentNounCapitalized", "Apartment")
                )}
              >
                <Input
                  type="number"
                  min={0}
                  max={MAX_ROOM_FILL}
                  step="1"
                  inputMode="numeric"
                  value={form.num_rooms}
                  onChange={(e) => patch({ num_rooms: e.target.value })}
                  placeholder="3"
                />
              </Field>
              <Field
                label={t("admin.apartments.rentalStatus", "Rental status")}
              >
                <Select
                  value={
                    form.rental_status === "" ? undefined : form.rental_status
                  }
                  onValueChange={(v) =>
                    patch({ rental_status: v as ApartmentRentalStatus })
                  }
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t(
                        "admin.apartments.selectStatus",
                        "Select status…"
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {APARTMENT_RENTAL_STATUSES.map((s) => (
                      <SelectItem key={s} value={s} className="capitalize">
                        {s === "vacant"
                          ? t("admin.apartments.vacant", "Vacant")
                          : t("admin.apartments.occupied", "Occupied")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </Section>

          {/* Owner + commercial terms */}
          <Section
            title={t("admin.apartments.ownerAndTerms", "Owner & terms")}
            description={t(
              "admin.apartments.ownerAndTermsDesc",
              "Pick the property owner — the owner is the lessor by default. A separate lessor entity only applies as an override (set in owner settings). Commercial terms below are specific to this apartment."
            )}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field
                label={t("admin.field.owner", "Owner")}
                className="sm:col-span-2"
              >
                <div className="flex items-center gap-2">
                  <LessorCombobox
                    className="flex-1 min-w-0"
                    value={form.lessor_id}
                    onChange={(next) =>
                      patch({ lessor_id: next, owner_user_id: null })
                    }
                    ownerValue={form.owner_user_id}
                    // Keep the portal link (owner_user_id) AND write the
                    // owner's lessor when they have one: lessor_id is what the
                    // Owners surfaces and the invoice generator bill by, so
                    // storing only the account left the apartment ownerless.
                    onSelectOwner={(userId, owner) =>
                      patch({
                        owner_user_id: userId,
                        lessor_id: owner.lessor_id ?? null,
                      })
                    }
                    placeholder={t(
                      "admin.apartments.selectOwner",
                      "Select owner…"
                    )}
                    noneLabel={t(
                      "admin.apartments.noOwnerLinked2",
                      "No owner linked"
                    )}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="shrink-0"
                    onClick={() => setCreateOwnerOpen(true)}
                  >
                    <Plus className="mr-1.5 h-4 w-4" />
                    {t("admin.apartments.addOwner", "Add owner")}
                  </Button>
                </div>
                {form.owner_user_id == null && form.lessor_id == null ? (
                  <p className="mt-1.5 flex items-center gap-1.5 text-xs text-amber-600">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    {t(
                      "admin.apartments.noOwnerLinkedYet",
                      "No owner linked yet — select one or add a new owner. You can still save and link it later."
                    )}
                  </p>
                ) : form.lessor_id && linkedOwner ? (
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    {t(
                      "admin.apartments.lessorOverrideNote",
                      "Lessor override — the representor below defaults to this lessor's."
                    )}
                  </p>
                ) : form.owner_user_id && !form.lessor_id ? (
                  // The account resolved to no lessor, so nothing downstream
                  // can attribute this apartment: it keys on lessor_id.
                  <p className="mt-1.5 flex items-start gap-1.5 text-xs text-amber-600">
                    <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
                    <span>
                      {t(
                        "admin.apartments.ownerAccountNoRecordPre",
                        "This owner account has no owner record, so the apartment won't appear under Owners and won't be invoiced. Use"
                      )}{" "}
                      <strong>
                        {t("admin.apartments.addOwner", "Add owner")}
                      </strong>{" "}
                      {t(
                        "admin.apartments.ownerAccountNoRecordPost",
                        "to create one, or link a lessor in owner settings."
                      )}
                    </span>
                  </p>
                ) : (
                  form.owner_user_id && (
                    <p className="mt-1.5 text-xs text-muted-foreground">
                      {t(
                        "admin.apartments.ownerActsAsLessor",
                        "Owner acts as the lessor. To use a different lessor entity, link one in owner settings."
                      )}
                    </p>
                  )
                )}
              </Field>

              {/* Representor — what prints after "vertreten durch" on this
                  apartment's contracts. Blank = inherit the owner's default,
                  so the placeholder shows what the contract would use today. */}
              <Field
                label={t("admin.apartments.representor", "Representor")}
                required={!isOwnerPortalOnly}
                className="sm:col-span-2"
              >
                <p className="text-xs text-muted-foreground">
                  {t(
                    "admin.apartment.representorDescription",
                    "The representor is the person or entity who represents the owner in the operations of this property/apartment."
                  )}
                </p>
                <Input
                  value={form.rep_override_name}
                  onChange={(e) => patch({ rep_override_name: e.target.value })}
                  placeholder={
                    inheritedRepName ||
                    t(
                      "admin.apartments.representorPlaceholder",
                      'e.g. "den Geschäftsführer" or "Coliviq GmbH"'
                    )
                  }
                />
                <p
                  className={`mt-1.5 text-xs ${
                    isOwnerPortalOnly ||
                    form.rep_override_name.trim() ||
                    inheritedRepName ||
                    propertyRepName ||
                    // Don't paint a failed/still-loading lookup as a hard
                    // "Required" error — handleSubmit's own toast explains
                    // that state instead of this field flashing red for it.
                    repLookupPending
                      ? "text-muted-foreground"
                      : "text-destructive"
                  }`}
                >
                  {form.rep_override_name.trim() ? (
                    <>
                      {t(
                        "admin.apartments.representorOverridePre",
                        "Overrides the owner's default for this apartment only. Clear it to go back to"
                      )}{" "}
                      <span className="font-medium text-foreground">
                        {inheritedRepName ||
                          propertyRepName ||
                          t("admin.apartments.noRepresentor", "no representor")}
                      </span>
                      .
                    </>
                  ) : inheritedRepName ? (
                    <>
                      {t(
                        "admin.apartments.inheritedFromOwner",
                        "Inherited from the owner:"
                      )}{" "}
                      <span className="font-medium text-foreground">
                        {inheritedRepName}
                      </span>
                      .{" "}
                      {t(
                        "admin.apartments.typeHereToOverride",
                        "Type here to override it for this apartment only."
                      )}
                    </>
                  ) : propertyRepName ? (
                    <>
                      {t(
                        "admin.apartments.repInheritedFromProperty",
                        "Inherited from the property:"
                      )}{" "}
                      <span className="font-medium text-foreground">
                        {propertyRepName}
                      </span>
                      .{" "}
                      {t(
                        "admin.apartments.typeHereToOverride",
                        "Type here to override it for this apartment only."
                      )}
                    </>
                  ) : repLookupPending ? (
                    // Not resolved yet either way — don't claim "Required"
                    // while still checking or after a failed lookup;
                    // handleSubmit's own toast covers that state on Save.
                    t(
                      "admin.apartments.checkingRepresentor",
                      "Checking whether an owner or the property already has a representor…"
                    )
                  ) : (
                    <>
                      {/* Blocks save (handleSubmit) for staff — say so rather
                          than leaving a silent blocker. Not enforced for an
                          owner editing their own apartment (isOwnerPortalOnly
                          above), so the prefix doesn't apply to them. */}
                      {!isOwnerPortalOnly && (
                        <>
                          {t(
                            "admin.apartments.representorRequiredPrefix",
                            "Required."
                          )}{" "}
                        </>
                      )}
                      {t(
                        "admin.apartment.representorNone",
                        "No representor set on the owner. Enter one here to set it for this apartment only."
                      )}
                    </>
                  )}
                </p>
              </Field>
              <Field
                label={t(
                  "admin.apartments.representorNote",
                  "Representor note"
                )}
                className="sm:col-span-2"
              >
                <Textarea
                  rows={2}
                  value={form.rep_override_note}
                  onChange={(e) => patch({ rep_override_note: e.target.value })}
                  placeholder={
                    linkedOwner?.rep_note?.trim() ||
                    t(
                      "admin.apartments.representorNotePlaceholder",
                      "Optional. Internal note about who signs for this apartment."
                    )
                  }
                />
                {/* The note annotates a name, it can't stand in for one: with no
                    name there is no override to save, so say that here instead
                    of accepting the text and dropping it on save. */}
                {form.rep_override_note.trim() &&
                  !form.rep_override_name.trim() && (
                    <p className="mt-1.5 text-xs text-amber-600 dark:text-amber-500">
                      {t(
                        "admin.apartments.addRepresentorForNote",
                        "Add a representor above for this note to be saved — a note on its own is not kept."
                      )}
                    </p>
                  )}
              </Field>
            </div>
          </Section>

          {/* Revenue & fees */}
          <Section
            id={SECTION_ID_REVENUE}
            title={t("admin.apartments.revenueAndFees", "Revenue & fees")}
            description={t(
              "admin.apartments.revenueAndFeesDesc",
              "How rent and the coliviq management fee flow for this apartment. Leave a field on “Inherit” to use the property default."
            )}
            defaultOpen={focusSection === "revenue"}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field
                label={t("admin.apartments.collectionMode", "Collection mode")}
                className="sm:col-span-2"
              >
                <Select
                  value={form.collection_mode}
                  onValueChange={(v) =>
                    patch({
                      collection_mode: v as FormState["collection_mode"],
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={INHERIT}>
                      {t(
                        "admin.apartments.inheritFromProperty",
                        "Inherit from property"
                      )}{" "}
                      (
                      {
                        COLLECTION_MODE_LABELS[
                          property?.collection_mode ?? "company_collects"
                        ]
                      }
                      )
                    </SelectItem>
                    {COLLECTION_MODES.map((m) => (
                      <SelectItem key={m} value={m}>
                        {COLLECTION_MODE_LABELS[m]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field
                label={t("admin.apartments.rentReminders", "Rent reminders")}
                className="sm:col-span-2"
                hint={t(
                  "admin.apartments.rentRemindersHint",
                  "Off suppresses overdue detection, reminder emails and dunning for this apartment (e.g. owner collects rent, no bank visibility)."
                )}
              >
                <Select
                  value={form.rent_reminders_enabled}
                  onValueChange={(v) =>
                    patch({
                      rent_reminders_enabled:
                        v as FormState["rent_reminders_enabled"],
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={INHERIT}>
                      {t(
                        "admin.apartments.inheritFromProperty",
                        "Inherit from property"
                      )}{" "}
                      (
                      {(property?.rent_reminders_enabled ?? true)
                        ? t("admin.apartments.remindersOn", "Reminders on")
                        : t("admin.apartments.remindersOff", "Reminders off")}
                      )
                    </SelectItem>
                    <SelectItem value="on">
                      {t("admin.apartments.remindersOn", "Reminders on")}
                    </SelectItem>
                    <SelectItem value="off">
                      {t(
                        "admin.apartments.remindersOffNotTracked",
                        "Reminders off (not tracked)"
                      )}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field
                label={t("admin.apartments.revenueModel", "Revenue model")}
              >
                <Select
                  value={form.revenue_model}
                  onValueChange={(v) =>
                    patch({ revenue_model: v as FormState["revenue_model"] })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={INHERIT}>
                      {t("admin.apartments.inherit", "Inherit")} (
                      {
                        REVENUE_MODEL_LABELS[
                          property?.revenue_model ?? "full_rent"
                        ]
                      }
                      )
                    </SelectItem>
                    {REVENUE_MODELS.map((rm) => (
                      <SelectItem key={rm} value={rm}>
                        {REVENUE_MODEL_LABELS[rm]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              {effectiveRevenueModel === "full_rent" && (
                <Field
                  label={t(
                    "admin.apartments.masterRentEurMo",
                    "Master rent (€/mo)"
                  )}
                >
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    inputMode="decimal"
                    value={form.master_rent}
                    onChange={(e) => patch({ master_rent: e.target.value })}
                  />
                </Field>
              )}
              <Field label={t("admin.apartments.contractEnd", "Contract end")}>
                <Input
                  type="date"
                  value={form.lease_end}
                  onChange={(e) => patch({ lease_end: e.target.value })}
                />
              </Field>
              <Field
                label={t("admin.apartments.payoutMethod", "Payout method")}
              >
                <Select
                  value={
                    form.payout_method === "" ? undefined : form.payout_method
                  }
                  onValueChange={(v) =>
                    patch({ payout_method: v as PayoutMethod })
                  }
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t(
                        "admin.apartments.selectMethod",
                        "Select method…"
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYOUT_METHODS.map((m) => (
                      <SelectItem key={m} value={m}>
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field
                label={t(
                  "admin.apartments.investedAmountEur",
                  "Purchase price (€)"
                )}
              >
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  inputMode="decimal"
                  value={form.invested_amount}
                  onChange={(e) => patch({ invested_amount: e.target.value })}
                />
              </Field>
              <Field label={t("admin.apartments.ownerSince", "Owner since")}>
                <Input
                  type="date"
                  value={form.owner_since}
                  onChange={(e) => patch({ owner_since: e.target.value })}
                />
              </Field>
              {form.revenue_model === "pct_of_rent" && (
                <Field
                  label={t("admin.apartments.coliviqFeePct", "coliviq fee (%)")}
                >
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    step="0.1"
                    inputMode="decimal"
                    value={form.coliviq_fee_pct}
                    onChange={(e) => patch({ coliviq_fee_pct: e.target.value })}
                    placeholder="25"
                  />
                </Field>
              )}
              {form.revenue_model === "flat_fee_per_room" && (
                <Field
                  label={t(
                    "admin.apartments.flatFeeEurRoom",
                    "Flat fee (€ / room)"
                  )}
                >
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    inputMode="decimal"
                    value={form.coliviq_fee_amount}
                    onChange={(e) =>
                      patch({ coliviq_fee_amount: e.target.value })
                    }
                    placeholder="120"
                  />
                  {Number(form.coliviq_fee_amount) > 0 &&
                    Number(form.num_rooms) > 0 && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {t(
                          "admin.apartments.totalMonthlyFee",
                          "Total monthly fee"
                        )}
                        :{" "}
                        <span className="font-medium text-foreground">
                          {(
                            Number(form.coliviq_fee_amount) *
                            Number(form.num_rooms)
                          ).toLocaleString("de-DE", {
                            style: "currency",
                            currency: "EUR",
                          })}
                        </span>{" "}
                        ({form.coliviq_fee_amount} × {form.num_rooms}{" "}
                        {t("admin.rooms.roomsNoun", "rooms")})
                      </p>
                    )}
                </Field>
              )}
              {form.revenue_model === "full_rent" && (
                <p className="text-xs text-muted-foreground sm:self-center">
                  {t(
                    "admin.apartments.marginFormula",
                    "Margin = tenant collections − master rent. No extra fee field."
                  )}
                </p>
              )}
            </div>
          </Section>

          {/* Payment methods — this apartment's override of the property's
              methods. Nothing here touches bank DETAILS; those are the "Bank
              accounts" section right below, and get_room_payment_methods
              resolves methods and account over the same room → apartment →
              property chain. Named "Payment methods", not "Payment
              configuration" — that label is already taken by the COL-287
              management-invoice link (PaymentConfigurationAnchor), which
              points at Revenue model, a different section entirely. */}
          <Section
            title={t("admin.apartments.paymentMethods", "Payment methods")}
            description={t(
              "admin.apartments.paymentMethodsDesc",
              "How tenants pay rent in this apartment. Leave a method on “Inherit” to follow the property."
            )}
          >
            <div className="space-y-3">
              {paymentMethodsLocked && (
                <p className="rounded-md border border-l-4 border-l-destructive bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                  {OWNER_COLLECTS_LOCK_REASON}
                </p>
              )}
              {PAYMENT_METHODS.map((method) => {
                const override = form[method.column];
                const inheritedEnabled = propertyMethods[method.key].enabled;
                const effective = paymentMethodsLocked
                  ? method.key === "bank_transfer"
                  : override === INHERIT
                    ? inheritedEnabled
                    : override === "on";
                return (
                  <PaymentMethodRow
                    key={method.key}
                    label={method.label}
                    description={method.description}
                    enabled={effective}
                    note={
                      paymentMethodsLocked ? (
                        <Badge variant="outline" className="font-normal">
                          {method.key === "bank_transfer"
                            ? "Always on while the owner collects"
                            : "Locked off while the owner collects"}
                        </Badge>
                      ) : (
                        <InheritanceBadge
                          // When inheriting, name the level the value really
                          // comes from: the property may itself be silent, in
                          // which case this is the CoLiviq default, not a
                          // decision someone made about this building.
                          source={
                            override === INHERIT
                              ? propertyMethods[method.key].source ===
                                "platform"
                                ? "platform"
                                : "property"
                              : "apartment"
                          }
                          level="apartment"
                        />
                      )
                    }
                  >
                    <Select
                      value={override}
                      disabled={paymentMethodsLocked}
                      onValueChange={(v) =>
                        patch({
                          [method.column]: v as MethodOverride,
                        } as Partial<FormState>)
                      }
                    >
                      <SelectTrigger className="w-[200px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={INHERIT}>
                          Inherit from property (
                          {methodStateLabel(inheritedEnabled)})
                        </SelectItem>
                        <SelectItem value="on">On</SelectItem>
                        <SelectItem value="off">Off</SelectItem>
                      </SelectContent>
                    </Select>
                  </PaymentMethodRow>
                );
              })}
              {sepaTurnedOn && !paymentMethodsLocked && (
                <p className="rounded-md border border-l-4 border-l-amber-500 bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                  <strong className="text-foreground">
                    Direct debit charges real money.
                  </strong>{" "}
                  Rooms in this apartment are debited automatically once the
                  tenant has signed a SEPA mandate. Nothing is charged without
                  one.
                </p>
              )}
            </div>
          </Section>

          {/* Bank accounts */}
          <Section title="Bank accounts">
            <div className="space-y-4">
              {/* Role mapping summary */}
              <div className="rounded-md border bg-background px-3 py-2 text-xs text-muted-foreground">
                {effectiveCollectionMode === "company_collects" ? (
                  <>
                    {t(
                      "admin.apartments.rentReceivedIntoPre",
                      "Rent is received into the"
                    )}{" "}
                    <strong>
                      {t("admin.apartments.coliviqAccount", "coliviq account")}
                    </strong>
                    ;{" "}
                    {t(
                      "admin.apartments.netRentPaidOutToPre",
                      "net rent is paid out to the"
                    )}{" "}
                    <strong>
                      {t("admin.apartments.ownerAccount", "owner account")}
                    </strong>
                    .
                  </>
                ) : (
                  <>
                    {t(
                      "admin.apartments.rentReceivedIntoPre",
                      "Rent is received into the"
                    )}{" "}
                    <strong>
                      {t("admin.apartments.ownerAccount", "owner account")}
                    </strong>
                    ;{" "}
                    {t(
                      "admin.apartments.feeIsPaidToPre",
                      "the fee is paid to the"
                    )}{" "}
                    <strong>
                      {t("admin.apartments.coliviqAccount", "coliviq account")}
                    </strong>
                    .
                  </>
                )}
              </div>

              {/* CoLiviq (platform) account */}
              <Field
                label={t("admin.apartments.coliviqAccount", "coliviq account")}
              >
                <Select
                  value={form.platform_bank_account_id}
                  onValueChange={(v) => patch({ platform_bank_account_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={INHERIT}>
                      {t("admin.apartments.inherit", "Inherit")} (
                      {resolvePlatformAccount({
                        property: property ?? null,
                        accounts: platformAccounts,
                        coliviqLessor: coliviqLessor ?? null,
                      })?.label ??
                        t(
                          "admin.apartments.defaultColiviqAccount",
                          "default coliviq account"
                        )}
                      )
                    </SelectItem>
                    {platformAccounts.map((acc) => (
                      <SelectItem key={acc.id} value={String(acc.id)}>
                        {acc.label}
                        {acc.is_default
                          ? ` (${t("admin.apartments.defaultLower", "default")})`
                          : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {effectivePlatformAccount ? (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {effectivePlatformAccount.holder} ·{" "}
                    {effectivePlatformAccount.iban}
                  </p>
                ) : (
                  <p className="mt-1 flex items-center gap-1.5 text-xs text-amber-600">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    {t(
                      "admin.apartments.noColiviqBankConfigured",
                      "No coliviq bank account configured yet."
                    )}
                  </p>
                )}
              </Field>

              {/* Owner account override */}
              <div className="space-y-2">
                <p className="text-xs font-medium">
                  {t(
                    "admin.apartments.ownerAccountOverride",
                    "Owner account (override)"
                  )}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t(
                    "admin.apartments.ownerAccountOverrideHint",
                    "Leave blank to inherit the owner's default bank details from the linked owner (lessor)."
                  )}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field
                    label={t(
                      "admin.properties.accountHolder",
                      "Account holder"
                    )}
                  >
                    <Input
                      value={form.bank_holder}
                      onChange={(e) => patch({ bank_holder: e.target.value })}
                      placeholder={linkedOwner?.bank_holder ?? "—"}
                    />
                  </Field>
                  <Field label={t("admin.properties.iban", "IBAN")}>
                    <Input
                      value={form.bank_iban}
                      onChange={(e) => patch({ bank_iban: e.target.value })}
                      placeholder={linkedOwner?.bank_iban ?? "—"}
                    />
                  </Field>
                  <Field label={t("admin.properties.bic", "BIC")}>
                    <Input
                      value={form.bank_bic}
                      onChange={(e) => patch({ bank_bic: e.target.value })}
                      placeholder={linkedOwner?.bank_bic ?? "—"}
                    />
                  </Field>
                  <Field label={t("admin.properties.bankName", "Bank name")}>
                    <Input
                      value={form.bank_name}
                      onChange={(e) => patch({ bank_name: e.target.value })}
                      placeholder={linkedOwner?.bank_name ?? "—"}
                    />
                  </Field>
                </div>
              </div>
            </div>
          </Section>

          {/* Key pickup override — blank inherits the property's pickup
              location. */}
          <Section
            title={t(
              "admin.apartments.keyPickupOverride",
              "Key pickup (KeyNest) — override"
            )}
            description={t(
              "admin.apartments.keyPickupOverrideDesc",
              "Leave blank to inherit the property's pickup location. Set these only when this apartment's keys are collected somewhere different. The KeyNest access link is set per room."
            )}
          >
            <div className="grid grid-cols-1 gap-3">
              <Field
                label={t("admin.apartments.pickupMethod", "Pickup method")}
              >
                <Select
                  value={form.pickup_method}
                  onValueChange={(v) =>
                    patch({ pickup_method: v as FormState["pickup_method"] })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={INHERIT}>
                      {t("admin.apartments.inherit", "Inherit")} (
                      {
                        PICKUP_METHOD_LABELS[
                          property?.pickup_method ?? "keynest"
                        ]
                      }
                      )
                    </SelectItem>
                    {PICKUP_METHODS.map((m) => (
                      <SelectItem key={m} value={m}>
                        {PICKUP_METHOD_LABELS[m]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field
                label={t("admin.apartments.pickupLocation", "Pickup location")}
              >
                <Input
                  value={form.keynest_pickup_location}
                  onChange={(e) =>
                    patch({ keynest_pickup_location: e.target.value })
                  }
                  placeholder={
                    property?.keynest_pickup_location
                      ? `${t("admin.apartments.inherits", "Inherits")}: ${property.keynest_pickup_location}`
                      : t(
                          "admin.apartments.pickupLocationPlaceholder",
                          "e.g. Kiosk am Hauptbahnhof"
                        )
                  }
                />
              </Field>
              <Field
                label={t("admin.apartments.pickupAddress", "Pickup address")}
              >
                <Textarea
                  rows={2}
                  value={form.keynest_pickup_address}
                  onChange={(e) =>
                    patch({ keynest_pickup_address: e.target.value })
                  }
                  placeholder={
                    property?.keynest_pickup_address
                      ? `${t("admin.apartments.inherits", "Inherits")}: ${property.keynest_pickup_address}`
                      : "Bahnhofstr. 5, 80335 München"
                  }
                />
              </Field>
              <Field
                label={t(
                  "admin.apartments.pickupInstructions",
                  "Pickup instructions"
                )}
              >
                <Textarea
                  rows={2}
                  value={form.pickup_instructions}
                  onChange={(e) =>
                    patch({ pickup_instructions: e.target.value })
                  }
                  placeholder={
                    property?.pickup_instructions
                      ? `${t("admin.apartments.inherits", "Inherits")}: ${property.pickup_instructions}`
                      : t(
                          "admin.apartments.pickupInstructionsPlaceholder",
                          "Ring the bell at reception during opening hours."
                        )
                  }
                />
              </Field>
            </div>
          </Section>

          {/* Virtual tour URL override — blank inherits the property's tour
              link. Shown to tenants on this apartment's rooms. */}
          <Section
            title={t("admin.apartments.virtualTourUrl", "Virtual tour URL")}
            description={t(
              "admin.apartments.virtualTourUrlDesc",
              "Leave blank to inherit the property's tour link. Set this only when this apartment has its own 360° tour."
            )}
          >
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="apartment-virtual-tour-url">
                  {t(
                    "admin.properties.tourUrlLabel",
                    "360° Tour URL (optional)"
                  )}
                </Label>
                <InheritanceBadge
                  source={
                    form.virtual_tour_url.trim()
                      ? "apartment"
                      : property?.virtual_tour_url?.trim()
                        ? "property"
                        : "none"
                  }
                  level="apartment"
                />
              </div>
              <Input
                id="apartment-virtual-tour-url"
                type="url"
                inputMode="url"
                value={form.virtual_tour_url}
                onChange={(e) => patch({ virtual_tour_url: e.target.value })}
                onBlur={(e) =>
                  patch({
                    virtual_tour_url: normalizeVirtualTourUrl(e.target.value),
                  })
                }
                placeholder={
                  property?.virtual_tour_url
                    ? `${t("admin.apartments.inherits", "Inherits")}: ${property.virtual_tour_url}`
                    : "https://vtc.virtualtourscreator.com.au/share/..."
                }
              />
            </div>
          </Section>
        </div>

        <SheetFooter className="mt-6 gap-2 sticky bottom-0 bg-background pt-4 border-t">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("common.saving", "Saving…")}
              </>
            ) : isEdit ? (
              t("admin.apartments.saveChanges", "Save changes")
            ) : (
              t("admin.apartments.createApartment", "Create apartment")
            )}
          </Button>
        </SheetFooter>

        <LessorFormDialog
          open={createOwnerOpen}
          onOpenChange={setCreateOwnerOpen}
          mode="create"
          isSubmitting={createLessorMut.isPending}
          onSubmit={handleCreateOwner}
        />
      </SheetContent>
    </Sheet>
  );
}
